import React, { createContext, useContext, useState, useCallback } from 'react';

const FilterContext = createContext();

export const useFilters = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilters must be used within a FilterProvider');
  }
  return context;
};

// Helper to get this month start date to today date range for initial load
const getThisMonthStartToToday = () => {
  const today = new Date();
  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  return { from: thisMonthStart, to: today };
};

export const FilterProvider = ({ children }) => {
  // Initialize with this month start date to today - shows this month's data on login
  const [filters, setFilters] = useState({
    dateRange: getThisMonthStartToToday(),
    selectedCompany: '',
    selectedLocation: '',
    selectedLocationName: '',
    selectedMachine: '',
    selectedMachineName: '',
    selectedEquipmentType: '1',
    selectedShift: ''
  });
  // Track if this is the initial load (true) or user has changed dates (false)
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  // Function to manually trigger API call
  const [manualTrigger, setManualTrigger] = useState(0);

  // Memoize updateFilters to prevent infinite loops
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => {
      // Track if date range is being changed by user (not initial load)
      if (newFilters.dateRange && (newFilters.dateRange.from || newFilters.dateRange.to)) {
        const prevDate = prev.dateRange;
        const newDate = newFilters.dateRange;
        // If date range actually changed from initial, mark that user has changed it
        if (prevDate?.from?.getTime() !== newDate?.from?.getTime() ||
          prevDate?.to?.getTime() !== newDate?.to?.getTime()) {
          setIsInitialLoad(false); // User has changed dates, no longer initial load
        }
      }

      // Only update if values actually changed to prevent unnecessary re-renders
      const hasChanges = Object.keys(newFilters).some(key => {
        if (key === 'dateRange') {
          const prevDate = prev.dateRange;
          const newDate = newFilters.dateRange;
          return prevDate?.from?.getTime() !== newDate?.from?.getTime() ||
            prevDate?.to?.getTime() !== newDate?.to?.getTime();
        }
        return prev[key] !== newFilters[key];
      });

      return hasChanges ? { ...prev, ...newFilters } : prev;
    });
  }, []);

  // Function to manually trigger API call
  const triggerManualFetch = useCallback(() => {
    setManualTrigger(prev => prev + 1);
    console.log('🔵 Manual API trigger activated');
  }, []);

  // State for machines available based on the current data response
  const [availableMachines, setAvailableMachines] = useState([]);

  return (
    <FilterContext.Provider value={{
      filters,
      updateFilters,
      isInitialLoad,
      manualTrigger,
      triggerManualFetch,
      availableMachines,
      setAvailableMachines
    }}>
      {children}
    </FilterContext.Provider>
  );
};

